/**
 * The Reflections tab on a profile, laid out like a blog: the person's reflections (on profile items, and
 * free-standing ones) on a timeline, newest first, grouped by month, with tags to filter by. Each entry opens
 * its own page (ReflectionArticlePage). The tab exists once there is at least one reflection; the owner can
 * write free-standing reflections from here.
 * Backend: GET /reflections/timeline/?author=<id> (registry/user/reflections_feed.py).
 */
import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { Add as AddIcon, LocalOffer as TagIcon, Lock as LockIcon, Mic as AudioIcon, Videocam as VideoIcon } from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';
import ReflectionEditor from './reflections/ReflectionEditor';

const monthOf = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
export const dayOf = (iso) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/** One reflection on a timeline. linkTo(e): where it opens (its own page; the public page's on a public page). */
export function Entry({ e, activeTag, onTag, linkTo = (x) => `/reflections/${x.id}/` }) {
  const navigate = useNavigate();
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <CardActionArea onClick={() => navigate(linkTo(e))}>
        {e.cover_url && <Box component="img" src={e.cover_url} alt="" sx={{ width: '100%', aspectRatio: '21 / 7', objectFit: 'cover', display: 'block' }} />}
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{dayOf(e.date)} · {e.reading_minutes} min read</Typography>
            {e.item && <Chip size="small" label={e.item.kind} variant="outlined" sx={{ height: 20, fontSize: 11.5 }} />}
            {e.media_type === 'audio' && <AudioIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            {e.media_type === 'video' && <VideoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            {e.status === 'draft' && <Chip size="small" label="Draft" sx={{ height: 20, fontSize: 11.5 }} />}
            {e.visibility === 'connections' && <LockIcon titleAccess="My connections" sx={{ fontSize: 14, color: 'text.disabled' }} />}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}>{e.title}</Typography>
          {e.subtitle && <Typography sx={{ fontSize: 14.5, color: 'text.secondary', fontStyle: 'italic', mt: 0.25 }}>{e.subtitle}</Typography>}
          {e.excerpt && <Typography sx={{ fontSize: 14, lineHeight: 1.6, mt: 0.75 }}>{e.excerpt}</Typography>}
        </CardContent>
      </CardActionArea>
      <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ px: 2, pb: 1.5 }}>
        {e.tags.map((t) => (
          <Chip key={t} size="small" label={t} onClick={() => onTag(t)}
            color={activeTag === t ? 'primary' : 'default'} variant={activeTag === t ? 'filled' : 'outlined'} sx={{ height: 22, fontSize: 12 }} />
        ))}
        <Button size="small" sx={{ ml: 'auto' }} onClick={() => navigate(linkTo(e))}>Read more</Button>
      </Box>
    </Card>
  );
}

export default function ReflectionsTab({ profileId }) {
  const navigate = useNavigate();
  const key = profileId ? `/reflections/timeline/?author=${profileId}` : null;
  const { data, error, isLoading, mutate } = useSWR(key, fetcher);
  const [tag, setTag] = useState(null);
  const [writing, setWriting] = useState(false);

  const entries = useMemo(() => (data?.entries || []).filter((e) => !tag || e.tags.includes(tag)), [data, tag]);
  const months = useMemo(() => {
    const out = [];
    entries.forEach((e) => {
      const m = monthOf(e.date);
      if (!out.length || out[out.length - 1].month !== m) out.push({ month: m, items: [] });
      out[out.length - 1].items.push(e);
    });
    return out;
  }, [entries]);

  if (isLoading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>The reflections could not be loaded.</Alert>;
  const toggle = (t) => setTag(tag === t ? null : t);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, mr: 'auto' }}>Reflections</Typography>
        <Button size="small" variant="outlined" onClick={() => navigate(`/profile/${profileId}/reflections/`)}>Open the page</Button>
        {data?.can_write && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setWriting(true)}>Write a reflection</Button>
        )}
      </Box>

      {data?.tags?.length > 0 && (
        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mb: 2 }}>
          <TagIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          {data.tags.slice(0, 20).map(({ tag: t, count }) => (
            <Chip key={t} size="small" label={`${t} (${count})`} onClick={() => toggle(t)}
              color={tag === t ? 'primary' : 'default'} variant={tag === t ? 'filled' : 'outlined'} />
          ))}
          {tag && <Chip size="small" label="Clear" onDelete={() => setTag(null)} />}
        </Box>
      )}

      {data?.hidden > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.hidden} more reflection{data.hidden === 1 ? ' is' : 's are'} shared with this researcher&apos;s connections only. Follow them to read {data.hidden === 1 ? 'it' : 'them'}.
        </Alert>
      )}

      {months.length === 0 && !tag && data?.can_write ? (
        // your own empty tab: what reflections are, and the two ways in
        <Box sx={{ py: 4, px: 2, textAlign: 'center', maxWidth: 560, mx: 'auto' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1 }}>Your reflections start here</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14.5, lineHeight: 1.6, mb: 2.5 }}>
            Write about your work in your own words: what a project taught you, the story behind a paper, where
            your research is heading. Write about an item of your profile with its reflect button, or about
            anything else from here. Your Reflections page collects them, with a cover you design, and can be
            made public.
          </Typography>
          <Box display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWriting(true)}>Write your first reflection</Button>
            <Button variant="outlined" onClick={() => navigate(`/profile/${profileId}/reflections/`)}>Open your Reflections page</Button>
          </Box>
        </Box>
      ) : months.length === 0 ? (
        <Typography sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>{tag ? 'Nothing with this tag yet.' : 'No reflections to show yet.'}</Typography>
      ) : months.map(({ month, items }) => (
        <Box key={month} sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>{month}</Typography>
          <Box sx={{ borderLeft: 2, borderColor: 'divider', ml: 0.75, pl: 2.5 }}>
            <Stack spacing={1.5}>
              {items.map((e) => (
                <Box key={e.id} sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'absolute', left: -27, top: 18, width: 10, height: 10, borderRadius: '50%',
                    bgcolor: e.item ? 'secondary.main' : 'primary.main', border: 2, borderColor: 'background.paper' }} />
                  <Entry e={e} activeTag={tag} onTag={toggle} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      ))}

      <ReflectionEditor open={writing} entry={null} tagSuggestions={(data?.tags || []).map((t) => t.tag)}
        onClose={() => setWriting(false)} onSaved={(id) => { setWriting(false); mutate(); navigate(`/reflections/${id}/`); }} />
    </Box>
  );
}
